import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Heart, MessageCircle, Share2, MapPin, Filter, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import CreatePostModal from '../components/community/CreatePostModal';
import PostCard from '../components/community/PostCard';

interface Post {
  id: string;
  content: string;
  location: string;
  tags: string[];
  emergency: boolean;
  created_at: string;
  likes: number;
  comments: number;
  profiles: {
    full_name: string;
    avatar_url?: string;
  };
}

const CommunityForum = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  // Check authentication status
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch posts from Supabase
  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          location,
          tags,
          emergency,
          created_at,
          likes,
          comment_count,
          profiles (
            full_name,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setPosts(data || []);
    } catch (error: any) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Filter posts based on search and filter criteria
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' ||
                         (selectedFilter === 'emergency' && post.emergency) ||
                         post.tags.includes(selectedFilter);
    
    return matchesSearch && matchesFilter;
  });

  const filterOptions = [
    { value: 'all', label: 'All Posts' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'shelter', label: 'Shelter' },
    { value: 'supplies', label: 'Supplies' },
    { value: 'medical', label: 'Medical' },
    { value: 'support', label: 'Support' },
  ];

  // Convert database post to component format
  const convertToComponentPost = (post: Post) => ({
    id: post.id,
    author: {
      name: post.profiles?.full_name || 'Anonymous User',
      avatar: post.profiles?.avatar_url || 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?w=150',
      verified: false,
    },
    content: post.content,
    location: post.location,
    timestamp: new Date(post.created_at),
    likes: post.likes || 0,
    comments: post.comment_count || 0,
    tags: post.tags || [],
    emergency: post.emergency || false,
  });

  const handleCreatePost = () => {
    if (!user) {
      toast.error('Please sign in to create a post');
      return;
    }
    setIsCreateModalOpen(true);
  };

  const handlePostCreated = () => {
    fetchPosts(); // Refresh posts after creating a new one
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20 pb-8 px-4"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
            Community Forum
          </h1>
          <p className="text-gray-300">Connect, share resources, and support each other during emergencies</p>
        </motion.div>

        {/* Action Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts, users, or locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="pl-10 pr-8 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors appearance-none cursor-pointer"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Create Post Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCreatePost}
            className="bg-gradient-to-r from-primary-400 to-primary-500 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all duration-200"
          >
            <Plus className="w-5 h-5" />
            Create Post
          </motion.button>
        </motion.div>

        {/* Auth Status */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-accent-orange/10 border border-accent-orange/30 rounded-lg p-4 mb-6"
          >
            <p className="text-accent-orange text-sm">
              Sign in to create posts and interact with the community.
            </p>
          </motion.div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin"></div>
          </div>
        ) : (
          /* Posts Feed */
          <div className="space-y-6">
            {filteredPosts.map((post, index) => (
              <PostCard 
                key={post.id} 
                post={convertToComponentPost(post)} 
                index={index} 
              />
            ))}
            
            {filteredPosts.length === 0 && !isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="text-gray-400 text-lg mb-4">No posts found</div>
                <p className="text-gray-500">
                  {posts.length === 0 
                    ? 'Be the first to create a post in the community!' 
                    : 'Try adjusting your search or filter criteria'
                  }
                </p>
              </motion.div>
            )}
          </div>
        )}

        {/* Floating Action Button (Mobile) */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleCreatePost}
          className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full flex items-center justify-center shadow-lg z-10"
        >
          <Plus className="w-6 h-6 text-white" />
        </motion.button>

        {/* Create Post Modal */}
        <CreatePostModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handlePostCreated}
        />
      </div>
    </motion.div>
  );
};

export default CommunityForum;